'use client'

import { useState } from 'react'
import { useCIAM } from 'security/hooks/CIAM/useCIAM/useCIAM'
import { ActionTypes } from 'security/types/CIAM'
import { useSetSession } from 'session/src/hooks/useSetSession'
import { setSessionCacheStorage } from 'session/src/utils/cacheStorage/setSessionCacheStorage'
import { userResponseToSession } from 'session/src/utils/userResponseToSession'

import { loginService } from '../services/loginService'
import type {
	LoginCredentials,
	LoginErrors,
	LoginManager,
} from '../types/login'
import { isLoginFieldsError } from '../utils/isLoginFieldsError'

export const useLogin = (isCheckout = false): LoginManager => {
	const [loginErrors, setLoginErrors] = useState<LoginErrors>(null)
	const [loginSuccess, setLoginSuccess] = useState(false)
	const { action, auth } = useCIAM()

	const setSession = useSetSession()

	if (!setSession) {
		throw new Error('useLogin must be used within a SessionProvider')
	}

	const login = async (credentials: LoginCredentials): Promise<void> => {
		await action(ActionTypes.LOGIN)

		const response = await loginService(credentials, isCheckout)

		if (!response) {
			setLoginErrors(['generic'])
		} else if (isLoginFieldsError(response)) {
			setLoginErrors(Object.keys(response.errorFields) as LoginErrors)
		} else {
			const sessionState = userResponseToSession(response)

			setSession(sessionState)

			await setSessionCacheStorage(sessionState)

			setLoginSuccess(true)
			setLoginErrors(null)
			auth()
		}
	}

	return {
		login,
		loginErrors,
		loginSuccess,
	}
}
